// apps/admin/src/features/audit/ArchiveSearchTab.tsx
//
// Phase 8.3 Wave 5 Task 39 — Archive Search tab (cold tier, >180d S3).
//
// vg-binding: CONTEXT:decisions=P8.D-113,P8.D-138,P8.D-191,P8.D-258,P8.D-480
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Cold-tier query surface:
//   - Requires date_from + date_to (BE rejects bare query with
//     INVALID_DATE_RANGE — surfaced inline).
//   - User must hold `audit:archive_search` permission (BE re-checks). The
//     parent page gates the tab strip + render based on the FE perm hook,
//     but the request is still defended server-side.
//   - Cold queries can take ~30s — we display:
//       • a yellow banner "Searching cold storage (S3) — results may take 30s"
//       • a progress spinner pulse during fetch
//       • a "Pseudonymized PII" pill on the result table cold-tier
//         policy (BE pseudonymizes actor_email etc. before returning rows).

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  AUDIT_V2_ENTITY_TYPES,
  searchAuditLogArchive,
  type AuditV2ApiError,
  type AuditV2ColdFilter,
  type AuditV2EntityType,
  type AuditV2Row,
} from '../../api/audit-log-v2.api.js';
import { MultiSelectDropdown } from './MultiSelectDropdown.js';
import { actionLabel } from './action-label.js';
import { useTranslation } from '../../i18n/useTranslation.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

// Shared audit entity-type map (`audit.entityTypeLabels.*`); raw value kept
// as the i18next defaultValue so unmapped types fall back gracefully.
function entityTypeLabel(t: TFn, value: string): string {
  return t(`audit.entityTypeLabels.${value}`, { defaultValue: value });
}

export interface ArchiveSearchTabProps {
  initial?: Partial<AuditV2ColdFilter>;
  onFilterChange?: (next: Partial<AuditV2ColdFilter>) => void;
  onRowClick: (row: AuditV2Row) => void;
}

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

function fmtTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function isApiError(err: unknown): err is AuditV2ApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { ok?: unknown }).ok === false &&
    typeof (err as { userMessage?: unknown }).userMessage === 'string'
  );
}

function truncate(value: string | null, max = 32): string {
  if (!value) return '—';
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

export function ArchiveSearchTab({
  initial,
  onFilterChange,
  onRowClick,
}: ArchiveSearchTabProps): JSX.Element {
  const { t } = useTranslation('ops');
  const { t: tAdmin } = useTranslation('admin');
  // Default range = (-365d .. -181d) — squarely inside cold tier window.
  const [filter, setFilter] = useState<AuditV2ColdFilter>(() => ({
    date_from: initial?.date_from ?? isoDaysAgo(365),
    date_to: initial?.date_to ?? isoDaysAgo(181),
    target_type: initial?.target_type,
    actor_user_id: initial?.actor_user_id,
    q: initial?.q,
    limit: 25,
  }));
  const [submitted, setSubmitted] = useState<AuditV2ColdFilter | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [buffer, setBuffer] = useState<AuditV2Row[]>([]);

  function patch<K extends keyof AuditV2ColdFilter>(
    key: K,
    next: AuditV2ColdFilter[K],
  ): void {
    setFilter((prev) => {
      const out = { ...prev, [key]: next };
      onFilterChange?.(out);
      return out;
    });
  }

  function runSearch(): void {
    setCursor(null);
    setBuffer([]);
    setSubmitted(filter);
  }

  const apiParams = useMemo<AuditV2ColdFilter | null>(() => {
    if (!submitted) return null;
    const out: AuditV2ColdFilter = { ...submitted };
    if (cursor) out.cursor = cursor;
    return out;
  }, [submitted, cursor]);

  const query = useQuery({
    queryKey: ['audit-log-v2', 'cold', apiParams],
    queryFn: () => searchAuditLogArchive(apiParams!, tAdmin),
    enabled: apiParams !== null,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (!query.data) return;
    setBuffer((prev) => {
      if (!apiParams?.cursor) return query.data!.rows;
      const seen = new Set(prev.map((r) => r.id));
      const merged = [...prev];
      for (const r of query.data!.rows) {
        if (!seen.has(r.id)) merged.push(r);
      }
      return merged;
    });
  }, [query.data, apiParams?.cursor]);

  const errMsg = useMemo<string | null>(() => {
    if (!query.error) return null;
    if (isApiError(query.error)) return query.error.userMessage;
    if (query.error instanceof Error) return query.error.message;
    return t('audit.archive.unavailable');
  }, [query.error, t]);

  const estimated = query.data?.meta?.estimated_seconds;
  const scanned = query.data?.meta?.s3_objects_scanned;

  return (
    <div className="flex flex-col gap-3">
      {/* Banner — cold storage notice + UX baseline */}
      <div
        className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-900"
        data-testid="audit-archive-banner"
        role="status"
      >
        <p className="font-medium">
          {t('audit.archive.bannerTitle')}
        </p>
        <p className="mt-0.5 text-xs">
          {t('audit.archive.bannerBody')}
        </p>
      </div>

      {/* Filters */}
      <div
        className="grid grid-cols-1 gap-2 rounded-lg border border-pw-border bg-pw-bg p-3 md:grid-cols-3"
        data-testid="audit-archive-filters"
      >
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.archive.dateFrom')}
          <input
            type="datetime-local"
            value={filter.date_from.slice(0, 16)}
            onChange={(e) =>
              patch(
                'date_from',
                e.target.value
                  ? new Date(e.target.value).toISOString()
                  : isoDaysAgo(365),
              )
            }
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
            data-testid="audit-archive-date-from"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.archive.dateTo')}
          <input
            type="datetime-local"
            value={filter.date_to.slice(0, 16)}
            onChange={(e) =>
              patch(
                'date_to',
                e.target.value
                  ? new Date(e.target.value).toISOString()
                  : isoDaysAgo(181),
              )
            }
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
            data-testid="audit-archive-date-to"
          />
        </label>
        <MultiSelectDropdown
          label={t('audit.filters.entityType')}
          options={AUDIT_V2_ENTITY_TYPES.map((et) => ({
            value: et,
            label: entityTypeLabel(t, et),
          }))}
          value={filter.target_type ?? []}
          onChange={(next) =>
            patch(
              'target_type',
              next.length > 0 ? (next as AuditV2EntityType[]) : undefined,
            )
          }
          placeholder={t('audit.filters.allEntities')}
          testId="audit-archive-entity-type"
        />
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted">
          {t('audit.filters.actorUserId')}
          <input
            type="text"
            placeholder={t('audit.archive.actorPlaceholder')}
            value={filter.actor_user_id ?? ''}
            onChange={(e) =>
              patch('actor_user_id', e.target.value.trim() || undefined)
            }
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 font-mono text-sm text-pw-text"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text-muted md:col-span-2">
          {t('audit.filters.search')}
          <input
            type="search"
            placeholder={t('audit.archive.searchPlaceholder')}
            value={filter.q ?? ''}
            onChange={(e) => patch('q', e.target.value || undefined)}
            className="h-9 rounded-md border border-pw-border bg-pw-bg px-2 text-sm text-pw-text"
            data-testid="audit-archive-q"
          />
        </label>

        <div className="md:col-span-3 flex items-center justify-end gap-2">
          {query.isFetching && submitted ? (
            <span className="flex items-center gap-2 text-xs text-pw-text-muted">
              <span
                aria-hidden="true"
                className="h-2 w-2 animate-pulse rounded-full bg-amber-500"
              />
              {t('audit.archive.scanning')}
              {typeof estimated === 'number' ? t('audit.archive.estimate', { seconds: estimated }) : ''}…
            </span>
          ) : null}
          <button
            type="button"
            onClick={runSearch}
            disabled={query.isFetching}
            className="inline-flex h-9 items-center justify-center rounded-md bg-pw-primary px-3 text-sm font-medium text-white hover:bg-pw-primary-strong disabled:opacity-50"
            data-testid="audit-archive-run"
          >
            {t('audit.archive.runButton')}
          </button>
        </div>
      </div>

      {errMsg ? (
        <div
          className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-800 dark:text-rose-300"
          data-testid="audit-archive-error"
          role="alert"
        >
          {errMsg}
        </div>
      ) : null}

      {!submitted ? (
        <div
          className="rounded-md border border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted"
          data-testid="audit-archive-idle"
        >
          {t('audit.archive.idlePrefix')}
          <strong>{t('audit.archive.runButton')}</strong>
          {t('audit.archive.idleSuffix')}
        </div>
      ) : (
        <section className="rounded-lg border border-pw-border bg-pw-bg">
          {/* Pseudonymized notice */}
          <div className="flex items-center justify-between border-b border-pw-border px-3 py-1.5 text-[11px] uppercase tracking-wide text-pw-text-muted">
            <span
              className="inline-flex items-center gap-1 rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-pw-text"
              data-testid="audit-archive-pseudonymized"
            >
              {t('audit.archive.pseudonymized')}
            </span>
            {typeof scanned === 'number' ? (
              <span>{t('audit.archive.scanned', { count: scanned })}</span>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-pw-border text-sm"
              data-testid="audit-archive-table"
            >
              <thead className="bg-pw-surface text-xs uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">{t('audit.columns.timestamp')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('audit.columns.actor')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('audit.columns.entity')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('audit.columns.action')}</th>
                  <th className="px-3 py-2 text-center font-medium">{t('audit.columns.diff')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-pw-border">
                {query.isLoading && buffer.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-8 text-center text-pw-text-muted"
                    >
                      {t('audit.archive.searching')}
                    </td>
                  </tr>
                ) : buffer.length === 0 && !query.isFetching ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-8 text-center text-pw-text-muted"
                      data-testid="audit-archive-empty"
                    >
                      {t('audit.archive.empty')}
                    </td>
                  </tr>
                ) : (
                  buffer.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer hover:bg-pw-surface"
                      onClick={() => onRowClick(row)}
                      data-testid={`audit-archive-row-${row.id}`}
                    >
                      <td className="px-3 py-2 align-top">
                        <span className="block font-mono text-[11px] text-pw-text">
                          {fmtTimestamp(row.occurred_at)}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="font-mono text-[11px] text-pw-text-muted">
                          {truncate(row.actor_user_id, 14)}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top text-pw-text">
                        {entityTypeLabel(t, row.target_type)} •{' '}
                        <span className="font-mono text-[11px] text-pw-text-muted">
                          {truncate(row.target_id, 14)}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] text-pw-text" title={row.action}>
                          {actionLabel(t, row.action)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center align-top">
                        {row.diff && row.diff.length > 0 ? (
                          <span className="inline-flex items-center rounded-md border border-pw-border bg-pw-surface px-1.5 py-0.5 text-[11px] font-medium text-pw-text">
                            {row.diff.length}
                            {row.diff_truncated ? '+' : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-pw-text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {buffer.length > 0 ? (
            <div className="flex items-center justify-between border-t border-pw-border px-3 py-2 text-xs text-pw-text-muted">
              <span>
                {t('audit.footer.showing', { count: buffer.length })}
                {query.isFetching ? t('audit.footer.loadingMore') : ''}
              </span>
              {query.data?.meta?.cursor_next ? (
                <button
                  type="button"
                  onClick={() => setCursor(query.data!.meta.cursor_next)}
                  disabled={query.isFetching}
                  className="rounded-md border border-pw-border bg-pw-bg px-2.5 py-1 font-medium text-pw-text hover:bg-pw-surface disabled:opacity-50"
                  data-testid="audit-archive-load-more"
                >
                  {query.isFetching ? t('audit.actions.loading') : t('audit.actions.loadMore')}
                </button>
              ) : null}
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}

export default ArchiveSearchTab;
